import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Row, Spin, notification } from "antd";
import Icon, { LeftOutlined } from "@ant-design/icons";

import Header from "../../components/layout/header";
import {
  CustomSpin,
  LeftIcon,
  PriorityIcon,
  TrolleyIcon,
  ToteIcon,
} from "../../assets/icons/icons";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import TrolleyServiceabilityModal from "../../components/mobile/TrolleyServiceabilityModal";
import { useTrolleyById } from "../common/queries";
import { useMarkTrolleyServiceable } from "../common/mutations";
import { getTrolleyStatus } from "../../components/reusable/styles";
import Styles from "./supervisorTrolleyInfo.module.scss";
import { convertUTCToLocal } from "../../utils/timeUtils";
const STATUS_EMPTY_NOT_AVAILABLE = "Empty - Not Available";
const STATUS_UNSERVICEABLE = "Unserviceable - Awaiting Repair";
const TARGET_LOCATION = "airlog empty holding";

type ServiceabilityAction = "serviceable" | "unserviceable";

const SupervisorTrolleyInfo = () => {
  const { id: encodedId } = useParams<{ id: string }>();
  const trolleyId = encodedId ? decodeURIComponent(encodedId) : "";
  const navigate = useNavigate();

  const {
    data: trolleyData,
    isLoading,
    error,
    refetch: refetchTrolley,
  } = useTrolleyById(trolleyId);

  const markServiceableMutation = useMarkTrolleyServiceable();

  useEffect(() => {
    if (trolleyId) {
      void refetchTrolley();
    }
  }, [trolleyId, refetchTrolley]);

  const [confirmationAction, setConfirmationAction] =
    useState<ServiceabilityAction | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [lastAction, setLastAction] = useState<ServiceabilityAction | null>(
    null
  );

  useEffect(() => {
    if (error && !isLoading && trolleyId) {
      notification.info({
        message: "Trolley Not Found",
        description: `Trolley (ID: ${trolleyId}) isn't registered in our system. Please contact the Operator.`,
        duration: 4,
      });

      const timer = setTimeout(() => {
        navigate("/supervisor-landing", {
          state: { defaultSegment: "Trolley" },
        });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error, isLoading, navigate, trolleyId]);

  const isInTargetLocation = useMemo(() => {
    if (!trolleyData) {
      return false;
    }

    const locationParts = [
      trolleyData.primaryLocation,
      trolleyData.secondaryLocation,
      trolleyData.lastScannedLocation,
    ].filter(Boolean) as string[];

    const combinedLocation = locationParts.join(" ").toLowerCase();
    return combinedLocation.includes(TARGET_LOCATION);
  }, [trolleyData]);

  const currentStatus = trolleyData?.status || "";
  const isEmptyNotAvailable = currentStatus === STATUS_EMPTY_NOT_AVAILABLE;
  const isUnserviceable = currentStatus === STATUS_UNSERVICEABLE;

  const canShowActions =
    isInTargetLocation && (isEmptyNotAvailable || isUnserviceable);

  const showBothActions = canShowActions && isEmptyNotAvailable;
  const showServiceableOnly = canShowActions && isUnserviceable;

  const handleBack = () => {
    navigate("/supervisor-landing", { state: { defaultSegment: "Trolley" } });
  };

  const openConfirmation = (action: ServiceabilityAction) => {
    setConfirmationAction(action);
    setIsConfirmationOpen(true);
  };

  const handleConfirmationClose = () => {
    if (markServiceableMutation.isPending) {
      return;
    }
    setIsConfirmationOpen(false);
    setConfirmationAction(null);
  };

  const handleConfirm = async () => {
    if (!trolleyData || !confirmationAction) {
      return;
    }

    const isServiceable = confirmationAction === "serviceable";
    const extractedNumericId = Number(
      (trolleyData.id ?? "").replace(/\D/g, "")
    );
    const trolleyIdentifier =
      typeof trolleyData.recordId === "number"
        ? trolleyData.recordId
        : Number.isNaN(extractedNumericId) || extractedNumericId === 0
        ? trolleyData.id
        : extractedNumericId;

    markServiceableMutation.mutate(
      {
        trolleyId: trolleyIdentifier,
        isServiceable,
        trolleyKey: trolleyData.id,
      },
      {
        onSuccess: (response) => {
          setLastAction(confirmationAction);
          setSuccessMessage(
            response?.message ||
              (isServiceable
                ? `Trolley #${trolleyData.id} is now marked as Serviceable and ready for use. You can assign it for loading and dispatch operations.`
                : `Trolley #${trolleyData.id} has been set to Unserviceable - awaiting repair. This trolley will no longer be available for operational use until repaired.`)
          );
          setIsSuccessOpen(true);
          setIsConfirmationOpen(false);
          setConfirmationAction(null);
        },
        onError: (error) => {
          const apiMessage =
            (
              error as {
                response?: { data?: { message?: string; detail?: string } };
              }
            )?.response?.data?.message ||
            (
              error as {
                response?: { data?: { message?: string; detail?: string } };
              }
            )?.response?.data?.detail;
          notification.error({
            message:
              apiMessage ||
              (isServiceable
                ? "Failed to mark the Trolley as Serviceable! Try again later."
                : "Failed to mark the Trolley as Unserviceable! Try again later."),
          });
        },
      }
    );
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    setLastAction(null);
    void refetchTrolley();
    navigate("/supervisor-landing", { state: { defaultSegment: "Trolley" } });
  };

  // Check if any tote has High priority
  const hasHighPriorityTote = Boolean(
    trolleyData?.totes?.some(
      (tote) => tote.priority && tote.priority.toLowerCase() === "high"
    ) || trolleyData?.priority
  );

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.contentContainer}>
        <Row
          align="middle"
          justify="space-between"
          style={{ marginBottom: "10px" }}
          className={Styles.headerRow}
        >
          <div className={Styles.headerIcon}>
            <Icon component={LeftIcon} onClick={handleBack} />
          </div>
          <div className={Styles.title}>Trolley Information</div>
          <div className={Styles.headerIcon} />
        </Row>

        <Spin spinning={isLoading} indicator={<CustomSpin />}>
          {trolleyData ? (
            <>
              <div className={Styles.infoSection}>
                <div className={Styles.sectionTitle}>Trolley Info</div>
                <Card className={Styles.infoCard}>
                  <div className={Styles.trolleyHeader}>
                    <div className={Styles.trolleyInfoRow}>
                      <Icon component={TrolleyIcon} className={Styles.icon} />
                      <div>
                        <div className={Styles.label}>Trolley ID</div>
                        <div className={Styles.value}>{trolleyData.id}</div>
                      </div>
                    </div>
                    {hasHighPriorityTote && (
                      <div className="priorityTag">
                        <Icon component={PriorityIcon} />
                        <span>Priority</span>
                      </div>
                    )}
                  </div>

                  <Row gutter={[16, 16]} className={Styles.detailsGrid}>
                    <Col span={12}>
                      <div className="labels">MDU</div>
                      <div className={Styles.detailValue}>
                        {trolleyData.mdu || "N/A"}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="labels">Status</div>
                      <div
                        className={Styles.detailValue}
                        style={{ color: getTrolleyStatus(trolleyData.status) }}
                      >
                        {trolleyData.status}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="labels">Registered Date</div>
                      <div className={Styles.detailValue}>
                        {trolleyData.registeredDate
                          ? convertUTCToLocal(trolleyData.registeredDate)
                          : "N/A"}
                      </div>
                    </Col>
                    <Col span={24}>
                      <div className="labels">Last Scanned Location</div>
                      <div className={Styles.detailValue}>
                        {trolleyData.lastScannedLocation || "N/A"}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </div>

              <div className={Styles.infoSection}>
                <div className={Styles.sectionTitle}>Totes</div>
                {(trolleyData.totes && trolleyData.totes.length > 0) ||
                (trolleyData.toteIds && trolleyData.toteIds.length > 0) ? (
                  <>
                    {(
                      trolleyData.totes ||
                      trolleyData.toteIds?.map((id) => ({ tote_id: id })) ||
                      []
                    ).map((tote, index) => {
                      const toteId = tote.tote_id;
                      const priority = tote.priority;
                      const totalTotes =
                        trolleyData.totes?.length ||
                        trolleyData.toteIds?.length ||
                        0;

                      return (
                        <Card
                          key={toteId}
                          className={Styles.infoCard}
                          style={{
                            marginBottom: index < totalTotes - 1 ? "12px" : "0",
                          }}
                        >
                          <Row
                            justify="space-between"
                            className={Styles.trolleyHeader}
                          >
                            <div className={Styles.trolleyInfoRow}>
                              <Icon
                                component={ToteIcon}
                                className={Styles.icon}
                              />
                              <div>
                                <div className={Styles.label}>Tote No</div>
                                <div className={Styles.value}>{toteId}</div>
                              </div>
                            </div>
                            {priority && priority.toLowerCase() === "high" && (
                              <div className={Styles.priorityBadge}>
                                <Icon component={PriorityIcon} />
                              </div>
                            )}
                          </Row>
                        </Card>
                      );
                    })}
                  </>
                ) : (
                  <Card className={Styles.infoCard}>
                    <Row className={Styles.trolleyHeader}>
                      <div className={Styles.trolleyInfoRow}>
                        <Icon component={ToteIcon} className={Styles.icon} />
                        <div className={Styles.value}>
                          No Totes have been loaded into Trolley
                        </div>
                      </div>
                    </Row>
                  </Card>
                )}
              </div>
            </>
          ) : (
            !isLoading && (
              <div className={Styles.emptyState}>
                <p>Unable to load trolley details.</p>
              </div>
            )
          )}
        </Spin>
      </div>

      {canShowActions && (
        <div className={Styles.bottomAction}>
          {showBothActions ? (
            <div className={Styles.dualActions}>
              <Button
                className={`${Styles.actionButton} ${Styles.secondaryButton}`}
                size="large"
                onClick={() => openConfirmation("unserviceable")}
                disabled={markServiceableMutation.isPending}
              >
                Mark as Unserviceable
              </Button>
              <Button
                className={Styles.actionButton}
                type="primary"
                size="large"
                onClick={() => openConfirmation("serviceable")}
                loading={
                  markServiceableMutation.isPending &&
                  confirmationAction === "serviceable"
                }
              >
                Mark as Serviceable
              </Button>
            </div>
          ) : (
            showServiceableOnly && (
              <Button
                className={Styles.fullWidthButton}
                type="primary"
                size="large"
                onClick={() => openConfirmation("serviceable")}
                loading={
                  markServiceableMutation.isPending &&
                  confirmationAction === "serviceable"
                }
              >
                Mark as Serviceable
              </Button>
            )
          )}
        </div>
      )}

      <TrolleyServiceabilityModal
        open={isConfirmationOpen}
        trolleyId={trolleyData?.id || trolleyId}
        status={trolleyData?.status}
        lastScannedLocation={trolleyData?.lastScannedLocation}
        mode={
          confirmationAction === "unserviceable"
            ? "unserviceable"
            : "serviceable"
        }
        onCancel={handleConfirmationClose}
        onConfirm={handleConfirm}
        isLoading={markServiceableMutation.isPending}
      />

      <SuccessAlert
        open={isSuccessOpen}
        title={
          lastAction === "unserviceable"
            ? "Trolley Marked as Unserviceable"
            : "Trolley Marked as Serviceable"
        }
        message={successMessage}
        buttonText="Go to Trolley List"
        onClose={handleSuccessClose}
      />
    </div>
  );
};

export default SupervisorTrolleyInfo;
